import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Card,
  CardContent,
  IconButton,
  Chip,
  Autocomplete
} from '@mui/material';
import { toast } from 'react-toastify';
import { ncdAPI } from '../../../../services/ncd.api';
import { FACILITY_MAP, getLocationFacilityDetails } from '../../utils/constants';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const CATEGORIES = [
  "RCH",
  "Communicable Diseases",
  "Minor Ailments/OPD",
  "Ophthalmic",
  "ENT problems",
  "Oral Care",
  "Geriatric Care",
  "Emergency/Trauma",
  "Mental Health",
  "Other"
];

const MOCK_SYMPTOMS = [];

const MOCK_MEDICINES = [];

export const General = ({ individualId, setShowGeneral, generalScreeningId }) => {
  const today = new Date().toISOString().split("T")[0];

  // API Integration states
  const [symptomSearchText, setSymptomSearchText] = useState("");
  const [symptomOptions, setSymptomOptions] = useState(MOCK_SYMPTOMS);
  const [medicineSearchText, setMedicineSearchText] = useState("");
  const [medicineOptions, setMedicineOptions] = useState(MOCK_MEDICINES);
  const [isLoading, setIsLoading] = useState(false);

  // States matching screenshots
  const [sessionDate, setSessionDate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [otherCategorySpecify, setOtherCategorySpecify] = useState("");

  // Consultation fields
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [observations, setObservations] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [remarks, setRemarks] = useState("");

  // Medication Advice fields
  const [medicines, setMedicines] = useState([]);

  // Refer fields
  const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails") || localStorage.getItem("NCD_loggedinUserdetails") || "{}");
  const facilityDetails = getLocationFacilityDetails(loggedData);
  const referFurtherToFacility = facilityDetails.referFurtherToFacility;

  const [referDate, setReferDate] = useState(null);
  const [referTo, setReferTo] = useState("None");
  const [referReason, setReferReason] = useState("None");

  // History log
  const [history, setHistory] = useState([]);

  const mapFrequencyToLong = (freq) => {
    if (freq === 'OD') return 'Once a day';
    if (freq === 'BD') return 'Twice a day';
    if (freq === 'TDS') return 'Thrice a day';
    if (freq === 'QDS') return 'Four times a day';
    return freq;
  };


  // Load existing general screening data if available
  useEffect(() => {
    const loadGeneralScreening = async () => {

      if (individualId && generalScreeningId) {
        try {
          const response = await ncdAPI.fetchGeneralScreening(individualId, generalScreeningId);

          let historyDetails = null;
          if (response?.data?.historyData?.generalHistoryDetails) {
            historyDetails = response.data.historyData.generalHistoryDetails;
          } else if (response?.historyData?.generalHistoryDetails) {
            historyDetails = response.historyData.generalHistoryDetails;
          } else if (Array.isArray(response?.data)) {
            historyDetails = response.data;
          } else if (response?.data && typeof response.data === 'object' && (response.data.assessmentDate || response.data.treatment || response.data.diagnosis)) {
            historyDetails = response.data;
          }

          const rawArray = Array.isArray(historyDetails) ? historyDetails : [historyDetails].filter(Boolean);
          
          if (rawArray.length > 0) {
            const reverseFacilityMap = {
              3200: "PHC",
              3300: "CHC",
              3400: "DH",
              3500: "TCC"
            };

            const formattedHistory = rawArray.map(payloadData => {
              const medsDesc = payloadData.treatment?.generalMedicines && Array.isArray(payloadData.treatment.generalMedicines)
                ? payloadData.treatment.generalMedicines.map(m => `${m.name || 'Unknown'} (${m.freq || ''} x ${m.dur || '0'} days)`).join(", ")
                : "None";

              const refToName = payloadData.refer ? (reverseFacilityMap[payloadData.refer.refToFaci] || "None") : "None";
              const referralDesc = refToName !== "None" ? `${refToName} - ${payloadData.refer.refReason || ''}` : "None";

              return {
                date: payloadData.assessmentDate || dayjs().format("DD-MM-YYYY"),
                facility: payloadData.facility || "PHC",
                category: payloadData.catergory && Array.isArray(payloadData.catergory) && payloadData.catergory.length > 0 ? payloadData.catergory[0] : "-",
                symptoms: payloadData.symptoms?.generalsymptoms ? payloadData.symptoms.generalsymptoms.join(", ") : "-",
                observations: payloadData.observations?.clinicalobservations || "-",
                diagnosis: payloadData.diagnosis?.generaldiagnosis || "-",
                medicines: medsDesc,
                referral: referralDesc,
                remarks: payloadData.remarks || "-"
              };
            });

            setHistory(formattedHistory);
          } else {
            setHistory([]);
          }
        } catch (error) {
          console.error("Error loading general screening data:", error);
          setHistory([]);
        }
      }
    };
    loadGeneralScreening();
  }, [individualId, generalScreeningId]);

  // Debounce search for symptoms
  useEffect(() => {
    const fetchSymptomOptions = async () => {
      if (symptomSearchText && symptomSearchText.length >= 1) {
        try {
          const response = await ncdAPI.fetchGeneralSymptomsSearch(symptomSearchText);
          if (response && response.success && response.data) {
            const list = Array.isArray(response.data) ? response.data : [];
            const mapped = list.map(item => typeof item === 'object' ? (item.symptomName || item.name || item.label || item.value) : item);
            setSymptomOptions(mapped);
          }
        } catch (error) {
          console.error('Error fetching symptoms:', error);
        }
      } else {
        setSymptomOptions(MOCK_SYMPTOMS);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchSymptomOptions();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [symptomSearchText]);

  // Debounce search for medicines
  useEffect(() => {
    const fetchMedicineOptions = async () => {
      if (medicineSearchText && medicineSearchText.length >= 1) {
        try {
          const response = await ncdAPI.getInitialAssessmentMedicines(medicineSearchText);
          if (response && response.data) {
            setMedicineOptions(response.data);
          }
        } catch (error) {
          console.error('Error fetching medicine options:', error);
        }
      } else {
        setMedicineOptions(MOCK_MEDICINES);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchMedicineOptions();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [medicineSearchText]);



  const handleClose = useCallback(() => {
    if (setShowGeneral) {
      setShowGeneral(false);
    }
  }, [setShowGeneral]);

  const handleAddMedicineRow = () => {
    setMedicines(prev => [
      ...prev,
      {
        id: `med_${Date.now()}_${prev.length}`,
        name: "",
        frequency: "",
        duration: "",
        quantity: "",
        instruction: ""
      }
    ]);
  };

  const handleUpdateMedicine = (id, field, value) => {
    setMedicines(prev => prev.map(m => {
      if (m.id === id) {
        // Calculate Quantity dynamically if duration and frequency are filled
        let updated = { ...m, [field]: value };
        if (field === 'duration' || field === 'frequency') {
          const freqMultiplier = getFrequencyMultiplier(updated.frequency);
          const days = parseInt(updated.duration, 10);
          if (!isNaN(days) && freqMultiplier > 0) {
            updated.quantity = String(freqMultiplier * days);
          }
        }
        return updated;
      }
      return m;
    }));
  };

  const getFrequencyMultiplier = (freq) => {
    if (freq === 'OD') return 1;
    if (freq === 'BD') return 2;
    if (freq === 'TDS') return 3;
    if (freq === 'QDS') return 4;
    return 0;
  };

  const handleDeleteMedicine = (id) => {
    setMedicines(prev => prev.filter(m => m.id !== id));
  };

  const handleSave = async () => {
    // Validation: Date and Category are mandatory
    if (!sessionDate) {
      toast.error("Session Date is mandatory");
      return;
    }
    if (!selectedCategory) {
      toast.error("Category is mandatory");
      return;
    }

    // Validation: at least one field of consultation is mandatory
    if (selectedSymptoms.length === 0 && !observations && !diagnosis && !remarks) {
      toast.error("At least one field in Consultation (Symptoms, Observations, Diagnosis, Remarks) is mandatory");
      return;
    }

    if (referTo && referTo !== "None" && !referDate) {
      toast.error("Referral Date is mandatory when referring a patient");
      return;
    }

    if (!generalScreeningId) {
      toast.error("Screening ID not found. Cannot submit screening data.");
      return;
    }

    const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || {};
    const fromFacility = Number(loggedData.facilityTypeId || loggedData.facilityId) || 3200;

    let referPayload = null;
    if (referTo && referTo !== "None") {
      referPayload = {
        refDate: referDate ? dayjs(referDate).format("DD-MM-YYYY") : "",
        refToFaci: FACILITY_MAP[referTo] || 3200,
        refReason: referReason || "",
        refFromFaci: fromFacility
      };
    }

    const payload = {
      refer: referPayload,
      assessDateWithTimeStamp: Date.now(),
      assessmentDate: sessionDate ? dayjs(sessionDate).format("DD-MM-YYYY") : "",
      facility: loggedData.facilityName || "PHC",
      catergory: [
        selectedCategory === "Other" ? `Other: ${otherCategorySpecify}` : selectedCategory
      ],
      symptoms: {
        generalsymptoms: selectedSymptoms
      },
      observations: {
        clinicalobservations: observations || ""
      },
      remarks: remarks || "",
      diagnosis: {
        generaldiagnosis: diagnosis || ""
      },
      treatment: {
        generalMedicines: medicines.map(m => ({
          name: m.name,
          freq: mapFrequencyToLong(m.frequency),
          dose: null,
          dur: m.duration ? String(m.duration) : "0",
          quan: Number(m.quantity) || 0,
          spInst: m.instruction || ""
        }))
      }
    };

    try {
      setIsLoading(true);
      const response = await ncdAPI.submitGeneralScreening(individualId, generalScreeningId, payload);
      if (response && response.success) {
        toast.success("General consultation details saved successfully!");

        // Format Date to DD-MM-YYYY
        const dateFormatted = sessionDate ? dayjs(sessionDate).format("DD-MM-YYYY") : "";

        // Medicines description string
        const medsDesc = medicines.length > 0
          ? medicines.map(m => `${m.name || 'Unknown'} (${m.frequency || ''} x ${m.duration || '0'} days)`).join(", ")
          : "None";

        // Referral description
        const referralDesc = referTo !== "None" ? `${referTo} - ${referReason}` : "None";

        const newLog = {
          date: dateFormatted,
          facility: loggedData.facilityName || "PHC Korra",
          category: selectedCategory === "Other" ? `Other: ${otherCategorySpecify}` : selectedCategory,
          symptoms: selectedSymptoms.join(", ") || "-",
          observations: observations || "-",
          diagnosis: diagnosis || "-",
          medicines: medsDesc,
          referral: referralDesc,
          remarks: remarks || "-"
        };

        const updatedHistory = [newLog, ...history];
        setHistory(updatedHistory);

        // Reset Form
        setSessionDate(null);
        setSelectedCategory("");
        setOtherCategorySpecify("");
        setSelectedSymptoms([]);
        setObservations("");
        setDiagnosis("");
        setRemarks("");
        setMedicines([]);
        setReferDate(null);
        setReferTo("None");
        setReferReason("None");
      } else {
        toast.error(response?.message || "Failed to save general screening data");
      }
    } catch (error) {
      console.error("Error submitting general screening:", error);
      toast.error(error.response?.data?.message || "Something went wrong while saving details");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>

      {/* Top Header Card */}
      <Card variant="outlined" sx={{ borderRadius: '8px', border: '1px solid #eef2f5', bgcolor: '#f8fafc', p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '15px' }}>
              Status: Examination initiated
            </Typography>
          </Box>
          <Box sx={{ width: '200px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
              format="DD/MM/YYYY"
                value={sessionDate ? dayjs(sessionDate) : null}
                onChange={(newValue) => setSessionDate(newValue ? newValue.format('YYYY-MM-DD') : null)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '6px' } }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </Card>

      {/* Category Card Selection */}
      <Card variant="outlined" sx={{ borderRadius: '8px', border: '1px solid #eef2f5' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 2 }}>
            Category <span style={{ color: '#ef4444' }}>*</span>
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(4, 1fr)",
              },
              gap: 2.5,
            }}
          >
            {CATEGORIES.map((category) => (
              <Button
                key={category}
                fullWidth
                variant="outlined"
                onClick={() => setSelectedCategory(category)}
                sx={{
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  color: selectedCategory === category ? "#333333" : "#475569",
                  bgcolor: selectedCategory === category ? "#9fa8da" : "#ffffff",
                  borderColor: selectedCategory === category ? "#7986cb" : "#cbd5e1",
                  borderWidth: selectedCategory === category ? "2px" : "1px",
                  borderRadius: "4px",
                  transition: "all 0.2s",
                  boxShadow: selectedCategory === category ? "0 2px 8px rgba(159, 168, 218, 0.3)" : "none",
                  "&:hover": {
                    bgcolor: selectedCategory === category ? "#8c98d4" : "#f8fafc",
                    borderColor: selectedCategory === category ? "#7986cb" : "#cbd5e1",
                    borderWidth: selectedCategory === category ? "2px" : "1px",
                  },
                }}
              >
                {category}
              </Button>
            ))}
          </Box>

          {selectedCategory === 'Other' && (
            <Box sx={{ mt: 3.5, maxWidth: '400px' }}>
              <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 600, mb: 1 }}>
                If other, specify
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={otherCategorySpecify}
                onChange={(e) => setOtherCategorySpecify(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
              />
            </Box>
          )}

        </CardContent>
      </Card>

      {/* Consultation Card */}
      <Card variant="outlined" sx={{ borderRadius: '8px', border: '1px solid #eef2f5' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}>
            Consultation <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>(At least one field is mandatory)</span> <span style={{ color: '#ef4444' }}>*</span>
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            {/* Symptoms Multi-select Autocomplete */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1, fontSize: '13.5px' }}>
                Symptoms
              </Typography>
              <Autocomplete
                multiple
                options={symptomOptions}
                value={selectedSymptoms}
                onChange={(event, newValue) => {
                  setSelectedSymptoms(newValue || []);
                }}
                onInputChange={(event, newInputValue) => {
                  setSymptomSearchText(newInputValue);
                }}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search/Select Symptoms"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option}
                      label={option}
                      size="small"
                      sx={{ bgcolor: 'rgba(159, 168, 218, 0.2)', color: '#3f51b5', fontWeight: 600 }}
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </Box>

            {/* Clinical Observations */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1, fontSize: '13.5px' }}>
                Clinical Observations
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter clinical observations"
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
              />
            </Box>

            {/* Diagnosis */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1, fontSize: '13.5px' }}>
                Diagnosis (If any)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter diagnosis"
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
              />
            </Box>

            {/* Remarks */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1, fontSize: '13.5px' }}>
                Remarks
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
              />
            </Box>

          </Box>
        </CardContent>
      </Card>

      {/* Medication Advice Card */}
      <Card variant="outlined" sx={{ borderRadius: '8px', border: '1px solid #eef2f5' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
            Medication Advice
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden', mb: 2.5 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#9fa8da' }}>
                <TableRow>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Medicine <span style={{ color: '#ef4444' }}>*</span></TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>
                    Frequency <span style={{ color: '#ef4444' }}>*</span>
                    <IconButton size="small" sx={{ color: '#3f51b5', ml: 0.5 }}>
                      <InfoIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Duration (in days) <span style={{ color: '#ef4444' }}>*</span></TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Quantity <span style={{ color: '#ef4444' }}>*</span></TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Special Instruction</TableCell>
                  <TableCell sx={{ py: 1.5 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {medicines.map((m) => (
                  <TableRow key={m.id}>

                    {/* Medicine */}
                    <TableCell sx={{ py: 1.5, minWidth: '220px' }}>
                      <Autocomplete
                        options={medicineOptions}
                        value={m.name || null}
                        onChange={(event, newValue) => {
                          handleUpdateMedicine(m.id, 'name', newValue || '');
                        }}
                        onInputChange={(event, newInputValue) => {
                          setMedicineSearchText(newInputValue);
                        }}
                        freeSolo
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Search medicine..."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                          />
                        )}
                      />
                    </TableCell>

                    {/* Frequency */}
                    <TableCell sx={{ py: 1.5, minWidth: '130px' }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={m.frequency}
                          onChange={(e) => handleUpdateMedicine(m.id, 'frequency', e.target.value)}
                        >
                          <MenuItem value="OD">OD</MenuItem>
                          <MenuItem value="BD">BD</MenuItem>
                          <MenuItem value="TDS">TDS</MenuItem>
                          <MenuItem value="QDS">QDS</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* Duration */}
                    <TableCell sx={{ py: 1.5 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={m.duration}
                        onChange={(e) => handleUpdateMedicine(m.id, 'duration', e.target.value)}
                        sx={{ width: '90px' }}
                      />
                    </TableCell>

                    {/* Quantity */}
                    <TableCell sx={{ py: 1.5 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={m.quantity}
                        onChange={(e) => handleUpdateMedicine(m.id, 'quantity', e.target.value)}
                        sx={{ width: '90px' }}
                      />
                    </TableCell>

                    {/* Instruction */}
                    <TableCell sx={{ py: 1.5, minWidth: '150px' }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={m.instruction || ''}
                          onChange={(e) => handleUpdateMedicine(m.id, 'instruction', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">Select</MenuItem>
                          <MenuItem value="Before Food">Before Food</MenuItem>
                          <MenuItem value="After Food">After Food</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ py: 1.5 }} align="center">
                      <IconButton size="small" onClick={() => handleDeleteMedicine(m.id)} sx={{ color: '#ef4444' }}>
                        <DeleteIcon sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </TableCell>

                  </TableRow>
                ))}

                {medicines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: '#94a3b8', py: 3 }}>
                      No medicines added yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleAddMedicineRow}
              sx={{
                bgcolor: '#00897b',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '4px',
                px: 3.5,
                '&:hover': { bgcolor: '#00796b' }
              }}
            >
              ADD MEDICINE
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Refer Card */}
      <Card variant="outlined" sx={{ borderRadius: '8px', border: '1px solid #eef2f5' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
            Refer
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#9fa8da' }}>
                <TableRow>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Date</TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Refer to</TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Reason for Referral</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>

                  {/* Date Selection */}
                  <TableCell sx={{ py: 2, minWidth: '180px' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                      format="DD/MM/YYYY"
                        value={referDate ? dayjs(referDate) : null}
                        onChange={(newValue) => setReferDate(newValue ? newValue.format('YYYY-MM-DD') : null)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { bgcolor: '#fff', width: '170px' }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </TableCell>

                  {/* Refer to */}
                  <TableCell sx={{ py: 2, minWidth: '180px' }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={referTo}
                        onChange={(e) => setReferTo(e.target.value)}
                      >
                        <MenuItem value="None">None</MenuItem>
                        {referFurtherToFacility.map((fac) => (
                          <MenuItem key={fac.id} value={fac.name}>{fac.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  {/* Reason for Referral */}
                  <TableCell sx={{ py: 2, minWidth: '220px' }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={referReason}
                        onChange={(e) => setReferReason(e.target.value)}
                      >
                        <MenuItem value="None">None</MenuItem>
                        <MenuItem value="Needs further tests">Needs further tests</MenuItem>
                        <MenuItem value="Unable to diagnose completely">Unable to diagnose completely</MenuItem>
                        <MenuItem value="Further Treatment">Further Treatment</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>

                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            bgcolor: '#3b51a3',
            color: '#fff',
            fontWeight: 700,
            px: 4,
            py: 1.2,
            textTransform: 'none',
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(59, 81, 163, 0.2)',
            '&:hover': { bgcolor: '#2e3f80' }
          }}
        >
          SAVE
        </Button>
      </Box>

      {/* Consultation History Card */}
      <Card variant="outlined" sx={{ borderRadius: '8px', border: '1px solid #eef2f5' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
            Consultation History
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#9fa8da' }}>
                <TableRow>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Date</TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Facility</TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Category</TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Symptoms</TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Observations</TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Diagnosis(if any)</TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Medicines</TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Referral</TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 800, py: 1.5 }}>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row, idx) => (
                  <TableRow key={idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>{row.date}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{row.facility}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{row.category}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{row.symptoms}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{row.observations}</TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 600 }}>{row.diagnosis}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{row.medicines}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{row.referral}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{row.remarks}</TableCell>
                  </TableRow>
                ))}

                {history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ color: '#94a3b8', py: 4 }}>
                      No consultation records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Floating Close Button */}
      <IconButton
        onClick={handleClose}
        sx={{
          border: "1px solid #eef2f5",
          width: "32px",
          height: "32px",
          bgcolor: "#fff",
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          cursor: "pointer",
          position: "absolute",
          right: "-12px",
          top: "-12px",
          zIndex: 10,
          "&:hover": {
            backgroundColor: "#f7fafc",
            color: '#e53e3e'
          },
        }}
      >
        <CloseIcon sx={{ fontSize: '16px' }} />
      </IconButton>

    </Box>
  );
};
